import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { ArrowUpRight, CheckCircle2, FileText, GripVertical, Lock, MessageSquare, UserRound, X } from "lucide-react";
import { serviceFamily } from "./serviceFamily";
import StatusIndicator from "./StatusIndicator";
import { formatCurrency, formatTime, getVisualState } from "./utils";

// The app-standard profile modal (self-portals to #modal-root). Lazy so the pill
// bundle doesn't pull the profile tab tree until someone opens a customer.
const CustomerPopup = lazy(() => import("../../Profile/CustomerPopup"));

const STATE_LABEL = {
  preview: "Preview",
  modified: "Modified",
  applied: "Applied",
  skipped: "Skipped",
  blocked: "Blocked",
  failed: "Failed",
};

const STOP_ACTIONS = [
  "Appointment Card",
  "Customer Card",
  "Cancel Appointment",
  "Reschedule",
  "Service Notification",
  "Appt Commission",
  "Shift Appts Up",
  "Shift Appts Down",
  "Lock Appointment",
];

const PINCARD_W = 300;

function AppointmentCard({
  appointment,
  routeId,
  variant = "dense",
  selected = false,
  selectedForApply = false,
  execution = null,
  onSelect,
  onToggleSelected,
  showReminders = false,
}) {
  const isBlock = variant === "block";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: appointment.id,
    data: {
      type: "appointment",
      routeId,
    },
  });
  const [pinned, setPinned] = useState(false);
  const [anchor, setAnchor] = useState(null); // pill rect captured at click
  const [pinPos, setPinPos] = useState(null); // resolved {top,left} after measuring the card
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [profileFor, setProfileFor] = useState(null);
  const pillRef = useRef(null);
  const pincardRef = useRef(null);
  const navigate = useNavigate();

  // The appointment-detail surface (src/components/Schedule/AppointmentDetail.jsx,
  // route /schedule/appointment/:id) is the ONE hub every card/row opens — no
  // second detail view. Resolve the same id TimeAxisBoard uses (CRM appt id wins,
  // else the stop id) so both board entry points land on the identical record.
  const detailId = appointment.crmAppointmentId || appointment.id || null;

  // Open the standard profile for this stop's ACTUAL linked record — keyed
  // structurally off the FK it carries (customer wins, else lead), stamping
  // object + matching id so the popup resolves that record (inbox 1c9f467 class).
  const openProfile = useCallback((event) => {
    event?.stopPropagation();
    // the pinned stop card is portaled above everything; close it so it never
    // sits on top of the customer window
    setPinned(false);
    const custId = appointment.customerId;
    const leadId = appointment.leadId;
    if (custId) {
      setProfileFor({
        kind: "customer",
        rec: { object: "customer", id: custId, customer_id: custId, display_name: appointment.customerName },
      });
    } else if (leadId) {
      setProfileFor({
        kind: "lead",
        rec: { object: "lead", id: leadId, crm_lead_id: leadId, display_name: appointment.customerName },
      });
    }
  }, [appointment.customerId, appointment.leadId, appointment.customerName]);
  const canOpenProfile = Boolean(appointment.customerId || appointment.leadId);

  // Click PINS the detail+actions card (portaled, flip-positioned). Hover only
  // ever shows the lightweight preview — no actions there.
  const openPinned = useCallback(() => {
    onSelect?.(appointment);
    const rect = pillRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchor({ top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom });
    }
    // Reset so the card renders hidden until measured (no flash at a stale position).
    setPinPos(null);
    setNotesExpanded(false);
    setPinned(true);
  }, [onSelect, appointment]);

  // Pinned card is persistent: closes only on X / Esc / outside-click.
  useEffect(() => {
    if (!pinned) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setPinned(false); };
    const onDown = (event) => {
      if (event.target.closest?.(".dispatch-pincard")) return;
      if (pillRef.current?.contains(event.target)) return;
      setPinned(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [pinned]);

  // Flip so the card sits BESIDE the pill, never over its own column: prefer the
  // right of the pill, fall back to the left when there's no room.
  // Measure the card AFTER render, then place it respecting BOTH axes: flip above
  // the pill when there isn't room below, and when neither side fits, pin within
  // the viewport (8px margins). Re-runs when the notes expand (height changes).
  // useLayoutEffect → positioned before paint, so no flash.
  useLayoutEffect(() => {
    if (!pinned || !anchor || !pincardRef.current) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardH = pincardRef.current.getBoundingClientRect().height; // capped by max-height
    // Horizontal: beside the pill (right, else flip left), clamped to the viewport.
    let left = anchor.right + 8;
    if (left + PINCARD_W > vw - 8) left = anchor.left - PINCARD_W - 8;
    if (left < 8) left = Math.max(8, vw - PINCARD_W - 8);
    // Vertical: below if it fits, else flip above, else clamp within the viewport.
    let top;
    if (anchor.top + cardH <= vh - 8) top = anchor.top;
    else if (anchor.bottom - cardH >= 8) top = anchor.bottom - cardH;
    else top = Math.max(8, vh - 8 - cardH);
    setPinPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [pinned, anchor, notesExpanded]);

  const visualState = getVisualState(appointment, execution);
  const isLocked =
    appointment.classification === "LOCKED" ||
    appointment.classificationReasons?.includes("finalized_window_locked");
  const hasReminder =
    Boolean(
      appointment?.metadata?.next_reminder_at ||
        appointment?.metadata?.last_reminder_at ||
        appointment?.metadata?.reminder_status ||
        appointment?.metadata?.reminderStatus ||
        appointment?.metadata?.reminder_due
    ) || Boolean(appointment.notes);
  const displayStart =
    appointment.computedStart ||
    formatTime(appointment.proposedStart || appointment.currentStart);
  const displayEnd = appointment.computedEnd
    ? appointment.computedEnd
    : appointment.proposedEnd || appointment.currentEnd
      ? formatTime(appointment.proposedEnd || appointment.currentEnd)
      : "";
  const serviceLabel = appointment.serviceCode || appointment.serviceName || "";
  // Colour by service family; duration and a confirmed mark on the chip.
  const family = serviceFamily(appointment.serviceName, appointment.serviceCode);
  const durationMin = Number(appointment.durationMinutes || 0);
  const isConfirmed = Boolean(appointment.confirmed) || /confirm/i.test(String(appointment.status || ""));

  // Open the full appointment record. Hand the board's computed sequence time
  // through nav state (boardStartLabel) so an FR stop — whose raw clock is an
  // 8am-UTC placeholder — shows the same time on the detail as on the board.
  const openDetail = useCallback(
    (event) => {
      event?.stopPropagation();
      if (!detailId) return;
      setPinned(false);
      // The appointment screen is the customer window's Appointments tab, with
      // this visit expanded — opened in place, never a page. A stop with no
      // customer record falls back to the route, which renders the record alone.
      if (appointment.customerId) {
        setProfileFor({
          kind: "customer",
          rec: { object: "customer", id: appointment.customerId, customer_id: appointment.customerId, display_name: appointment.customerName },
          tab: "appointments",
          focus: detailId,
          boardStartLabel: displayStart || null,
        });
        return;
      }
      navigate(`/schedule/appointment/${detailId}`, { state: { boardStartLabel: displayStart || null } });
    },
    [navigate, detailId, displayStart, appointment.customerId, appointment.customerName]
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        pillRef.current = node;
      }}
      style={{ ...style, "--pin": family.color }}
      className={[
        "dispatch-pill",
        "has-family",
        isBlock ? "dispatch-pill--block" : "",
        `is-${visualState}`,
        selected ? "is-selected" : "",
        selectedForApply ? "is-selected-for-apply" : "",
        pinned ? "is-pinned" : "",
        isDragging ? "is-dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className="dispatch-pill-row"
        role="button"
        tabIndex={0}
        onClick={openPinned}
        onContextMenu={(event) => { event.preventDefault(); openPinned(); }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPinned();
          }
        }}
        title={`${appointment.customerName}${appointment.serviceName ? ` · ${appointment.serviceName}` : ""}${durationMin ? ` · ${durationMin} min` : ""}`}
      >
        {isBlock ? (
          <>
            <span className="dispatch-block-l1">
              <span className="dispatch-block-time">
                {displayStart}{displayEnd ? `–${displayEnd}` : ""}
              </span>
              <span className="dispatch-pill-icons">
                {hasReminder ? (
                  <span className={`dispatch-pill-reminder ${showReminders ? "is-active" : ""}`} title="Reminder activity or notes available">
                    <MessageSquare size={12} />
                  </span>
                ) : null}
                {isLocked ? <StatusIndicator kind="lock" value="locked" label="Locked" /> : null}
                <StatusIndicator kind="appointment" value={appointment.status} label={appointment.status || "scheduled"} />
                <button
                  type="button"
                  className="dispatch-pill-handle"
                  aria-label="Drag appointment"
                  onClick={(event) => event.stopPropagation()}
                  {...attributes}
                  {...listeners}
                >
                  <GripVertical size={12} />
                </button>
              </span>
            </span>
            <strong className="dispatch-block-name">{appointment.customerName}</strong>
            <span className="dispatch-block-l3">
              {serviceLabel ? <span className="dispatch-block-service" title={appointment.serviceName}>{serviceLabel}</span> : null}
              {appointment.value ? <span className="dispatch-block-value">{formatCurrency(appointment.value)}</span> : null}
            </span>
          </>
        ) : (
          <>
            <span className="dispatch-pill-time">{displayStart}</span>
            <span className="dispatch-pill-copy">
              <strong>{appointment.customerName}</strong>
              {serviceLabel ? (
                <span className="dispatch-pill-service dispatch-pill-code" title={appointment.serviceName || serviceLabel}>
                  {serviceLabel}
                </span>
              ) : null}
            </span>
            <span className="dispatch-pill-icons">
              {isConfirmed ? <span className="dispatch-pill-confirmed" title={`Confirmed${durationMin ? ` · ${durationMin} min` : ""}`} aria-label="Confirmed"><CheckCircle2 size={12} /></span> : null}
              {hasReminder ? (
                <span
                  className={`dispatch-pill-reminder ${showReminders ? "is-active" : ""}`}
                  title="Reminder activity or notes available"
                  aria-label="Reminder activity or notes available"
                >
                  <MessageSquare size={12} />
                </span>
              ) : null}
              {isLocked ? <StatusIndicator kind="lock" value="locked" label="Locked" /> : null}
              <StatusIndicator
                kind="appointment"
                value={appointment.status}
                label={appointment.status || "scheduled"}
              />
              {appointment.value ? <span className="dispatch-pill-value">{formatCurrency(appointment.value)}</span> : null}
              <button
                type="button"
                className="dispatch-pill-handle"
                aria-label="Drag appointment"
                onClick={(event) => event.stopPropagation()}
                {...attributes}
                {...listeners}
              >
                <GripVertical size={12} />
              </button>
            </span>
          </>
        )}
      </div>

      {/* HOVER = lightweight preview only. Name · time · service. No actions. */}
      <div className="dispatch-pill-preview" role="tooltip" aria-hidden="true">
        <strong>{appointment.customerName}</strong>
        <span>
          {displayStart}{displayEnd ? `–${displayEnd}` : ""}
          {serviceLabel ? ` · ${serviceLabel}` : ""}
        </span>
      </div>

      {/* CLICK = pinned detail+actions card. Portaled to body so a transformed
          pill/column can never capture or clip it (this is what killed the old
          detached "Lock Appointment" fragment). */}
      {pinned
        ? createPortal(
            <div
              ref={pincardRef}
              className="dispatch-pincard"
              style={pinPos
                ? { top: pinPos.top, left: pinPos.left, width: PINCARD_W }
                : { top: 8, left: 8, width: PINCARD_W, visibility: "hidden" }}
              role="dialog"
              aria-label={`${appointment.customerName} appointment`}
            >
              {/* HEADER — always visible */}
              <div className="dispatch-pincard-head">
                {canOpenProfile ? (
                  <button type="button" className="dispatch-hc-name" onClick={openProfile} title={`Open ${appointment.customerName}'s profile`}>
                    {appointment.customerName}
                  </button>
                ) : (
                  <strong className="dispatch-pincard-name">{appointment.customerName}</strong>
                )}
                <div className="dispatch-pincard-head-right">
                  {appointment.customerId ? <span className="dispatch-hc-acct">Acct #{appointment.customerId}</span> : null}
                  <button type="button" className="dispatch-pincard-close" onClick={() => setPinned(false)} aria-label="Close">
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* BODY — scrolls when the content (long notes) is taller than the card */}
              <div className="dispatch-pincard-body">
                {appointment.address ? <div className="dispatch-hc-addr">{appointment.address}</div> : null}
                <div className="dispatch-hc-grid">
                  {appointment.externalId ? (
                    <div><span>Appt #</span><b>{appointment.externalId}</b></div>
                  ) : null}
                  <div><span>Duration</span><b>{appointment.durationMinutes || 60} min</b></div>
                  <div>
                    <span>Time</span>
                    <b>{displayStart}{displayEnd ? `–${displayEnd}` : ""}</b>
                  </div>
                  {appointment.value ? (
                    <div><span>Service $</span><b>{formatCurrency(appointment.value)}</b></div>
                  ) : null}
                  {appointment.dueDate ? (
                    <div><span>Preferred / Due</span><b>{appointment.dueDate}</b></div>
                  ) : null}
                  <div className="dispatch-hc-status-row">
                    <span>Status</span><b>{STATE_LABEL[visualState] || appointment.status || "Scheduled"}</b>
                  </div>
                  {appointment.serviceName ? (
                    <div className="dispatch-hc-wide"><span>Service</span><b>{appointment.serviceName}</b></div>
                  ) : null}
                </div>
                {appointment.notes ? (
                  <div className="dispatch-hc-notes">
                    <span>Notes</span>
                    <p className={notesExpanded ? "" : "dispatch-hc-notes-clamp"}>{appointment.notes}</p>
                    {String(appointment.notes).length > 160 ? (
                      <button
                        type="button"
                        className="dispatch-hc-notes-toggle"
                        onClick={() => setNotesExpanded((v) => !v)}
                      >
                        {notesExpanded ? "Show less" : "Show more"}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className="dispatch-pincard-menu" role="menu" aria-label="Appointment actions">
                  {STOP_ACTIONS.map((action) => (
                    <button
                      key={`${appointment.id}-${action}`}
                      type="button"
                      className="dispatch-pill-menu-item"
                      onClick={() => {
                        if (action === "Appointment Card") openDetail();
                        else if (action === "Customer Card") openProfile();
                        else setPinned(false);
                      }}
                    >
                      {action === "Appointment Card" ? <FileText size={13} /> : null}
                      {action === "Customer Card" ? <UserRound size={13} /> : null}
                      {action === "Lock Appointment" ? <Lock size={13} /> : null}
                      <span>{action}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ACTIONS ROW — always visible. "Open appointment" is the primary
                  action: it leaves the board for the full appointment record. */}
              <div className="dispatch-pincard-actions">
                {detailId ? (
                  <button
                    type="button"
                    className="dispatch-pill-hover-btn dispatch-pincard-open"
                    onClick={openDetail}
                  >
                    Open appointment <ArrowUpRight size={13} />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`dispatch-pill-hover-btn ${selectedForApply ? "is-active" : ""}`}
                  aria-pressed={selectedForApply}
                  onClick={() => onToggleSelected?.(appointment.id)}
                >
                  {selectedForApply ? "Queued" : "Queue"}
                </button>
                {isLocked ? (
                  <span className="dispatch-pill-hover-tag"><Lock size={12} /> Locked</span>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}

      {profileFor ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={profileFor.rec}
            leadType={profileFor.kind}
            initialTab={profileFor.tab}
            focusAppointment={profileFor.focus || null}
            boardStartLabel={profileFor.boardStartLabel || null}
            onClose={() => setProfileFor(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

export default React.memo(AppointmentCard);
